import {
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
];

const EventInfo = ({
  values,
  handleChange,
  setFieldValue,
  touched,
  errors,
}) => {
  const [instruction, setInstruction] = useState(values?.instruction || "");

  useEffect(() => {
    setInstruction(values?.instruction || "");
  }, [values?.instruction]);

  const handleInstructionChange = (content) => {
    setInstruction(content);
    setFieldValue("instruction", content);
  };

  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0"); // January is 0!
    const year = today.getFullYear();

    return `${year}-${month}-${day}`;
  };

  const currentDate = getCurrentDate();
  const formatDate = (date) => {
    const d = new Date(date);
    let month = `${d.getMonth() + 1}`;
    let day = `${d.getDate()}`;
    const year = d.getFullYear();
    if (month.length < 2) month = `0${month}`;
    if (day.length < 2) day = `0${day}`;
    return [year, month, day].join("-");
  };

  return (
    <Grid container spacing={1}>
      <Grid item md={12} xs={12}>
        <TextField
          fullWidth
          type="text"
          id="name"
          label="Event Title"
          name="title"
          value={values?.title}
          error={touched.title && Boolean(errors.title)}
          helperText={touched.title && errors.title}
          onChange={handleChange}
        />
      </Grid>
      <Grid item md={6} xs={6}>
        <TextField
          fullWidth
          label="Start Date *"
          margin="normal"
          name="startDate"
          value={values?.startDate}
          error={touched.startDate && Boolean(errors.startDate)}
          helperText={touched.startDate && errors.startDate}
          type="date"
          InputProps={{
            inputProps: {
              min: formatDate(new Date()),
            },
          }}
          InputLabelProps={{
            shrink: true,
          }}
          onChange={handleChange}
        />
      </Grid>


      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid item md={6} xs={6}>
          <TimePicker
            label="Start Time *"
            value={
              values?.startTime
                ? (() => {
                  try {
                    const date = new Date(`1970-01-01T${values.startTime}`);
                    return isNaN(date.getTime()) ? null : date;
                  } catch {
                    return null;
                  }
                })()
                : null
            }
            onChange={(newValue) => {
              if (newValue instanceof Date && !isNaN(newValue.getTime())) {
                const formatted = format(newValue, "HH:mm"); // use 24-hr format for consistency
                setFieldValue("startTime", formatted);
              } else {
                setFieldValue("startTime", null); // fallback on invalid input
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                margin="normal"
                name="startTime"
                error={touched.startTime && Boolean(errors.startTime)}
                helperText={touched.startTime && errors.startTime}
              />
            )}
            ampm // optional
          />

        </Grid>
      </LocalizationProvider>


      <Grid item md={6} xs={6}>
        <TextField
          fullWidth
          label="End Date *"
          margin="normal"
          name="endDate"
          value={values?.endDate}
          error={touched.endDate && Boolean(errors.endDate)}
          helperText={touched.endDate && errors.endDate}
          InputProps={{
            inputProps: {
              min: formatDate(new Date()),
            },
          }}
          InputLabelProps={{
            shrink: true,
          }}
          type="date"
          onChange={handleChange}
        />
      </Grid>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid item md={6} xs={6}>
          <TimePicker
            label="End Time *"
            value={
              values?.endTime
                ? (() => {
                  try {
                    const date = new Date(`1970-01-01T${values.endTime}`);
                    return isNaN(date.getTime()) ? null : date;
                  } catch {
                    return null;
                  }
                })()
                : null
            }
            onChange={(newValue) => {
              if (newValue instanceof Date && !isNaN(newValue.getTime())) {
                const formatted = format(newValue, "HH:mm"); // 24-hour format for consistency
                setFieldValue("endTime", formatted);
              } else {
                setFieldValue("endTime", null); // or "", depending on your schema
              }
            }}
            ampm // keep if you're using 12-hour UI
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                margin="normal"
                name="endTime"
                error={touched.endTime && Boolean(errors.endTime)}
                helperText={touched.endTime && errors.endTime}
              />
            )}
          />

        </Grid>
      </LocalizationProvider>

      <Grid item md={12} xs={12}>
        <Typography variant="h4">Instructions OR Description</Typography>
        <Grid mt={1}>
          <ReactQuill
            value={instruction}
            onChange={handleInstructionChange}
            theme="snow"
            modules={modules}
            formats={formats}
          />
        </Grid>
      </Grid>
      <Grid item md={12} xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={values?.limitRequired}
              onChange={(e) => {
                setFieldValue("limitRequired", e.target.checked);
              }}
            />
          }
          label="Enable Participant Limit"
        />
      </Grid>
    </Grid>
  );
};

export default EventInfo;
